import React, { useMemo } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';
import { FlujoFondosPeriodo } from '../../../services/tesoreriaFlujoFondosService';
import { formatCurrency } from '../../../utils/formatters';

interface Props {
  periodos: FlujoFondosPeriodo[];
  umbralExcedente: number;
  onPeriodoClick: (periodo: FlujoFondosPeriodo) => void;
}

const FlujoFondosChart: React.FC<Props> = ({ periodos, umbralExcedente, onPeriodoClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const data = useMemo(
    () =>
      periodos.map((p) => ({
        ...p,
        egresosNeg: -Math.abs(p.egresos),
        saldoDotColor: p.alertaNegativo ? '#c62828' : p.alertaExcedente ? '#ed6c02' : '#1976d2',
      })),
    [periodos]
  );

  const minWidth = Math.max(320, periodos.length * (isMobile ? 72 : 88));

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#1a1a1a' }}>
        Evolución proyectada
      </Typography>
      <Box sx={{ width: '100%', overflowX: 'auto', pb: 1 }}>
        <Box sx={{ width: '100%', minWidth, height: isMobile ? 280 : 340 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={data}
              margin={{ top: 8, right: 12, left: 4, bottom: isMobile ? 8 : 4 }}
              onClick={(state) => {
                const idx = state?.activeTooltipIndex;
                if (typeof idx === 'number' && periodos[idx]) {
                  onPeriodoClick(periodos[idx]);
                }
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                dataKey="etiqueta"
                tick={{ fill: '#666', fontSize: isMobile ? 10 : 12 }}
                interval={0}
                angle={isMobile ? -30 : 0}
                textAnchor={isMobile ? 'end' : 'middle'}
                height={isMobile ? 50 : 30}
              />
              <YAxis
                yAxisId="flujo"
                tick={{ fill: '#666', fontSize: 11 }}
                tickFormatter={(v) =>
                  Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)
                }
              />
              <YAxis
                yAxisId="saldo"
                orientation="right"
                tick={{ fill: '#666', fontSize: 11 }}
                tickFormatter={(v) =>
                  Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)
                }
              />
              <Tooltip
                formatter={(value: number, name: string) => [
                  formatCurrency(Math.abs(value)),
                  name === 'egresosNeg' ? 'Egresos' : name === 'ingresos' ? 'Ingresos' : 'Saldo',
                ]}
                labelFormatter={(label) => `Período: ${label}`}
                contentStyle={{
                  borderRadius: 8,
                  border: '1px solid #e0e0e0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                }}
              />
              <Legend
                formatter={(value) =>
                  value === 'egresosNeg' ? 'Egresos' : value === 'ingresos' ? 'Ingresos' : 'Saldo acumulado'
                }
              />
              <ReferenceLine yAxisId="flujo" y={0} stroke="#9e9e9e" />
              {umbralExcedente > 0 ? (
                <ReferenceLine
                  yAxisId="saldo"
                  y={umbralExcedente}
                  stroke="#ed6c02"
                  strokeDasharray="4 4"
                  label={{ value: 'Umbral', position: 'insideTopRight', fill: '#ed6c02', fontSize: 11 }}
                />
              ) : null}
              <Bar yAxisId="flujo" dataKey="ingresos" name="ingresos" fill="#66bb6a" maxBarSize={36} cursor="pointer" />
              <Bar yAxisId="flujo" dataKey="egresosNeg" name="egresosNeg" fill="#ef5350" maxBarSize={36} cursor="pointer" />
              <Line
                yAxisId="saldo"
                type="monotone"
                dataKey="saldoAcumulado"
                name="saldoAcumulado"
                stroke="#1976d2"
                strokeWidth={2.5}
                dot={(props: { cx?: number; cy?: number; payload?: { saldoDotColor?: string } }) => {
                  const { cx, cy, payload } = props;
                  if (cx == null || cy == null) return null as unknown as React.ReactElement;
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={4}
                      fill={payload?.saldoDotColor || '#1976d2'}
                      stroke="#fff"
                      strokeWidth={1}
                    />
                  );
                }}
                activeDot={{ r: 6 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary">
        Tocá un período del gráfico para ver el detalle de los movimientos.
      </Typography>
    </Box>
  );
};

export default FlujoFondosChart;
